import type {
  HandbookImport,
  HandbookImportMode,
  HandbookImportStatus,
} from '@dreamai/types';

export const MAX_UPLOAD_FILES = 20;
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const UPLOAD_ACCEPT = '.pdf,.docx,.md,.markdown,.txt,.html,.htm';
export const IMPORT_POLL_INTERVAL_MS = 3000;

export const IMPORT_MODE_LABELS: Record<HandbookImportMode, string> = {
  compile: 'Compiled',
  as_is: 'As-is',
};

export function isImportRunning(status: HandbookImportStatus): boolean {
  return (
    status === 'queued' || status === 'extracting' || status === 'compiling'
  );
}

/** Replaces the entry with the same id, or prepends it when it is new. */
export function upsertImport(
  list: HandbookImport[],
  next: HandbookImport,
): HandbookImport[] {
  const index = list.findIndex((i) => i.id === next.id);
  if (index === -1) return [next, ...list];
  return list.map((i, at) => (at === index ? next : i));
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Why a set of files cannot be uploaded, or null when it can. */
const UPLOAD_EXTENSIONS = new Set(UPLOAD_ACCEPT.split(','));

function isSupportedUpload(file: File): boolean {
  const dot = file.name.lastIndexOf('.');
  return dot >= 0 && UPLOAD_EXTENSIONS.has(file.name.slice(dot).toLowerCase());
}

export function uploadProblem(files: File[]): string | null {
  if (files.length > MAX_UPLOAD_FILES) {
    return `Up to ${MAX_UPLOAD_FILES} files per import; remove ${files.length - MAX_UPLOAD_FILES}.`;
  }
  const unsupported = files.filter((f) => !isSupportedUpload(f));
  if (unsupported.length > 0) {
    return `Only PDF, Word (.docx), Markdown, text and HTML files can be read: ${unsupported.map((f) => f.name).join(', ')}.`;
  }
  const tooBig = files.filter((f) => f.size > MAX_UPLOAD_BYTES);
  if (tooBig.length > 0) {
    return `Each file must be 25 MB or smaller: ${tooBig.map((f) => f.name).join(', ')}.`;
  }
  return null;
}
